import { v4 } from 'uuid'
import { create } from 'zustand'
import {
  type ActiveGate,
  CHAT_VISIBLE_EVENTS,
  type ChecklistItem,
  type ConversationSnapshot,
  type FeedbackVote,
  type GateAnswer,
  type PendingInteraction,
  type RunStatus,
  readAutoAnswer,
  readChecklist,
  readGate,
  readMessage,
  type TranscriptEntry,
  UPDATE_TYPES,
  type VibeEvent,
  type WireQuestionData,
} from './wire'

/**
 * One store, one reducer. The transcript is append-only with stable ids; both
 * the SSE viewer (per event) and rehydration (bulk snapshot) write through the
 * same pure functions, so live and restored chat are the same code path.
 */

export interface QueuedMessage {
  id: string
  text: string
}

export interface VibeAgentState {
  transcript: TranscriptEntry[]
  runStatus: RunStatus
  gate: ActiveGate | null
  sessionId: string | null
  /** Server response_id of the active run — needed for POST /cancel. */
  responseId: string | null
  checklist: ChecklistItem[]
  checklistExplanation: string | null
  checklistExpanded: boolean
  userInput: string
  /** FE-local staging: messages typed while a run is busy, dispatched when
   *  it's the user's turn. Never persisted, never in the transcript. */
  queuedMessages: QueuedMessage[]
  /** Set when a run ends in `error` with messages staged: the queue holds —
   *  visibly — until the user resumes or clears it. Stops and interrupts do
   *  not pause; their staged messages dispatch like any other turn. */
  queuePaused: boolean
  /** FE-local run timing for the tracker's past-tense completion header
   *  ("Completed in 2m 10s"). Not persisted — a reloaded session shows the
   *  plain completed state, never a fabricated duration. */
  runStartedAt: number | null
  runEndedAt: number | null
  /** The caller's thumbs per turn, keyed by response_id. Rehydrated from the
   *  snapshot; written optimistically by FeedbackControls. */
  feedback: Record<string, FeedbackVote>
}

const initialState: VibeAgentState = {
  transcript: [],
  runStatus: 'idle',
  gate: null,
  sessionId: null,
  responseId: null,
  checklist: [],
  checklistExplanation: null,
  checklistExpanded: false,
  userInput: '',
  queuedMessages: [],
  queuePaused: false,
  runStartedAt: null,
  runEndedAt: null,
  feedback: {},
}

const gateFromInteraction = (p: PendingInteraction | null): ActiveGate | null =>
  p?.interaction_id
    ? {
        interactionId: p.interaction_id,
        kind: p.type,
        questions: p.questions ?? [],
        autoAnswerSeconds: p.auto_answer_seconds ?? null,
      }
    : null

const entryFromEvent = (event: VibeEvent, questionData?: WireQuestionData): TranscriptEntry => ({
  id: v4(),
  role: 'assistant',
  message_type: 'event',
  update_type: event.update_type,
  text: readMessage(event) || null,
  payload: event.payload ?? null,
  question_data: questionData ?? null,
  response_id: event.response_id ?? null,
  ts: Date.now(),
})

/** Marks the open gate entry answered in place — the same enrichment the BE
 *  persists (transcript.mark_gate_answered), so a later rehydration agrees. */
const resolveGateEntry = (
  transcript: TranscriptEntry[],
  interactionId: string | undefined,
  answers: GateAnswer[] | null,
): TranscriptEntry[] => {
  // With an id, match it. WITHOUT one (a malformed event), resolve ONLY the
  // last still-open card — never every card, which would wrongly mark unrelated
  // earlier questions answered.
  const fallbackIdx = interactionId
    ? -1
    : transcript.reduce(
        (acc, e, i) => (e.question_data && !e.question_data.is_answered ? i : acc),
        -1,
      )
  return transcript.map((e, i) =>
    e.question_data &&
    (interactionId ? e.question_data.interaction_id === interactionId : i === fallbackIdx)
      ? {
          ...e,
          question_data: { ...e.question_data, is_answered: true, selected_answers: answers },
        }
      : e,
  )
}

/** Is `last` the in-flight streaming AI bubble for this event's response? */
const isStreamingTailFor = (last: TranscriptEntry | undefined, event: VibeEvent): boolean =>
  !!last?.streaming &&
  last.update_type === UPDATE_TYPES.AI_MESSAGE &&
  (last.response_id ?? null) === (event.response_id ?? null)

/** Folds a chunk into the tail AI entry for its response (id stable — no re-key). */
const foldChunk = (transcript: TranscriptEntry[], event: VibeEvent): TranscriptEntry[] => {
  const delta = readMessage(event)
  if (!delta) return transcript
  const last = transcript[transcript.length - 1]
  if (isStreamingTailFor(last, event)) {
    const updated = { ...last, text: (last?.text ?? '') + delta }
    return [...transcript.slice(0, -1), updated]
  }
  return [
    ...transcript,
    {
      id: v4(),
      role: 'assistant',
      message_type: 'text',
      update_type: UPDATE_TYPES.AI_MESSAGE,
      text: delta,
      response_id: event.response_id ?? null,
      ts: Date.now(),
      streaming: true,
    },
  ]
}

/** Terminal/interrupt events end the stream mid-message: clear the tail's
 *  streaming flag so the row stops animating (nothing else ever clears it —
 *  the atomic AI_MESSAGE never arrives on these paths). */
const finalizeStreamingTail = (transcript: TranscriptEntry[]): TranscriptEntry[] => {
  const last = transcript[transcript.length - 1]
  if (!last?.streaming) return transcript
  return [...transcript.slice(0, -1), { ...last, streaming: undefined }]
}

/** The server's own user_message (multi-tab / replay): adopt the local pending
 *  bubble in place when it matches; drop a replay of a row the snapshot already
 *  delivered (keyed by response_id — the BE stamps the same one on the event and
 *  the persisted transcript row); else append. */
const adoptUserMessage = (transcript: TranscriptEntry[], event: VibeEvent): TranscriptEntry[] => {
  const text = readMessage(event).trim()
  const tail = transcript.findLast((e) => e.role === 'user')
  if (tail?.pending && (tail.text ?? '').trim() === text) {
    // Clear the dim style; keep `local` — the event proves delivery, only a
    // snapshot proves persistence. Stamp the event's response_id so the adopted
    // row carries the id the BE persists; replay/resync dedup keys on it.
    return transcript.map((e) =>
      e.id === tail.id
        ? { ...e, pending: undefined, response_id: event.response_id ?? e.response_id ?? null }
        : e,
    )
  }
  if (
    event.response_id &&
    transcript.some(
      (e) =>
        e.role === 'user' && e.response_id === event.response_id && (e.text ?? '').trim() === text,
    )
  ) {
    return transcript
  }
  if (text.startsWith('[FE_EVENT]')) return transcript
  return [
    ...transcript,
    {
      id: v4(),
      role: 'user',
      message_type: 'text',
      update_type: UPDATE_TYPES.USER_MESSAGE,
      text,
      response_id: event.response_id ?? null,
      ts: Date.now(),
    },
  ]
}

interface ReducedState {
  transcript: TranscriptEntry[]
  runStatus: RunStatus
  gate: ActiveGate | null
  responseId: string | null
  checklist: ChecklistItem[]
  checklistExplanation: string | null
  runStartedAt?: number | null
  runEndedAt?: number | null
  // Optional: present when reducing live store state (abnormal-end branches
  // pause a non-empty queue); snapshot rehydration reduces without them.
  queuedMessages?: QueuedMessage[]
  queuePaused?: boolean
  feedback?: Record<string, FeedbackVote>
}

/**
 * The pure event reducer — every SSE frame goes through here and nowhere else.
 * Canvas mutations are NOT chat state; canvas-sync consumes the same events
 * separately. Unknown update_types are ignored (forward-compatible).
 */
export const applyEvent = (state: ReducedState, event: VibeEvent): ReducedState => {
  const t = event.update_type
  switch (t) {
    case UPDATE_TYPES.STARTED:
      return {
        ...state,
        runStatus: 'running',
        responseId: event.response_id ?? null,
        // First started of a session begins the clock; chained turns keep it.
        runStartedAt: state.runStartedAt ?? Date.now(),
        runEndedAt: null,
      }

    case UPDATE_TYPES.USER_MESSAGE:
      return { ...state, transcript: adoptUserMessage(state.transcript, event) }

    case UPDATE_TYPES.AI_MESSAGE_CHUNK:
      return { ...state, transcript: foldChunk(state.transcript, event) }

    case UPDATE_TYPES.AI_MESSAGE: {
      // Atomic message: replace a streaming tail for the same response, else append.
      const last = state.transcript[state.transcript.length - 1]
      const text = readMessage(event)
      if (isStreamingTailFor(last, event)) {
        const updated = { ...last, text: text || last.text, streaming: undefined }
        return { ...state, transcript: [...state.transcript.slice(0, -1), updated] }
      }
      if (!text) return state
      return {
        ...state,
        transcript: [...state.transcript, { ...entryFromEvent(event), message_type: 'text', text }],
      }
    }

    case UPDATE_TYPES.TOOL_CALL_STARTED: {
      // Ephemeral (stream-only, never persisted): the row renders immediately
      // with a spinner; the completed event upgrades it in place. A replayed
      // started for a call we already show must not duplicate the row.
      const callId = (event.payload as { tool_call_id?: string } | null)?.tool_call_id
      const exists =
        !!callId &&
        state.transcript.some(
          (e) =>
            (e.payload as { tool_call_id?: string } | null)?.tool_call_id === callId &&
            (e.update_type === UPDATE_TYPES.TOOL_CALL_STARTED ||
              e.update_type === UPDATE_TYPES.TOOL_CALL_COMPLETED),
        )
      if (exists) return state
      return { ...state, transcript: [...state.transcript, entryFromEvent(event)] }
    }

    case UPDATE_TYPES.TOOL_CALL_COMPLETED: {
      // Upgrade the matching started row IN PLACE (stable entry id — the row
      // must not re-key/re-animate; only the icon flips). Without a match
      // (rehydration, or started was filtered) append as before.
      const callId = (event.payload as { tool_call_id?: string } | null)?.tool_call_id
      const idx = callId
        ? state.transcript.findIndex(
            (e) =>
              e.update_type === UPDATE_TYPES.TOOL_CALL_STARTED &&
              (e.payload as { tool_call_id?: string } | null)?.tool_call_id === callId,
          )
        : -1
      if (idx >= 0) {
        const upgraded = {
          ...state.transcript[idx],
          update_type: UPDATE_TYPES.TOOL_CALL_COMPLETED,
          payload: event.payload ?? null,
        } as TranscriptEntry
        return {
          ...state,
          transcript: state.transcript.map((e, i) => (i === idx ? upgraded : e)),
        }
      }
      return { ...state, transcript: [...state.transcript, entryFromEvent(event)] }
    }

    case UPDATE_TYPES.DRAFT_SAVED:
      return { ...state, transcript: [...state.transcript, entryFromEvent(event)] }

    case UPDATE_TYPES.ASK_USER_QUESTION: {
      const { questions, interactionId, autoAnswerSeconds } = readGate(event)
      // Replay idempotence: a stale-cursor reattach can re-deliver a gate the
      // snapshot already carried. Never duplicate the card; reopen the gate
      // only if it is still unanswered.
      const existing = interactionId
        ? state.transcript.find(
            (e) => e.update_type === t && e.question_data?.interaction_id === interactionId,
          )
        : undefined
      if (existing) {
        if (existing.question_data?.is_answered) return state
        return {
          ...state,
          runStatus: autoAnswerSeconds === null ? 'waiting_input' : state.runStatus,
          gate: { interactionId, kind: 'question', questions, autoAnswerSeconds },
        }
      }
      return {
        ...state,
        runStatus: autoAnswerSeconds === null ? 'waiting_input' : state.runStatus,
        gate: { interactionId, kind: 'question', questions, autoAnswerSeconds },
        transcript: [
          ...state.transcript,
          entryFromEvent(event, { questions, interaction_id: interactionId }),
        ],
      }
    }

    case UPDATE_TYPES.WAITING_FOR_APPROVAL: {
      // Gate state only — legacy parity: the approval surface is the button
      // row (+ countdown), never a transcript line.
      const { interactionId, autoAnswerSeconds } = readGate(event)
      return {
        ...state,
        runStatus: autoAnswerSeconds === null ? 'waiting_input' : state.runStatus,
        gate: { interactionId, kind: 'approval', questions: [], autoAnswerSeconds },
      }
    }

    case UPDATE_TYPES.AUTO_ANSWERED: {
      const { interactionId, answers } = readAutoAnswer(event)
      // Replay idempotence (duplicate "Continued automatically" bug): the
      // gate-time persisted transcript already carries this banner when a
      // stale cursor replays the event — never append it twice per interaction.
      const already =
        !!interactionId &&
        state.transcript.some(
          (e) =>
            e.update_type === t &&
            (e.payload as { interaction_id?: string } | null)?.interaction_id === interactionId,
        )
      const resolved = resolveGateEntry(state.transcript, interactionId, answers)
      return {
        ...state,
        gate: null,
        runStatus: 'running',
        transcript: already ? resolved : [...resolved, entryFromEvent(event)],
      }
    }

    case UPDATE_TYPES.CHECKLIST_UPDATED: {
      const { checklist, explanation } = readChecklist(event)
      return {
        ...state,
        checklist: checklist ?? state.checklist,
        checklistExplanation: explanation ?? state.checklistExplanation,
      }
    }

    case UPDATE_TYPES.GATE_TIMER_HELD: {
      // Disarm the countdown on the matching open gate; the gate itself stays
      // armed for answers. waiting_input mirrors the parked-gate semantics
      // (and what the BE persists) so banners/composer copy read correctly.
      const heldId = (event.payload?.interaction_id as string | undefined) ?? null
      if (!state.gate || state.gate.autoAnswerSeconds === null) return state
      if (heldId && state.gate.interactionId !== heldId) return state
      return {
        ...state,
        runStatus: 'waiting_input',
        gate: { ...state.gate, autoAnswerSeconds: null },
      }
    }

    case UPDATE_TYPES.ERROR:
      return {
        ...state,
        runStatus: 'error',
        gate: null,
        queuePaused: state.queuePaused || (state.queuedMessages?.length ?? 0) > 0,
        transcript: [...finalizeStreamingTail(state.transcript), entryFromEvent(event)],
      }

    case UPDATE_TYPES.INTERRUPTED:
      // Synthetic viewer event (liveness bridge): the run behind the stream is
      // dead. No transcript row — the resume banner renders off runStatus.
      return {
        ...state,
        runStatus: 'interrupted',
        gate: null,
        transcript: finalizeStreamingTail(state.transcript),
      }

    case UPDATE_TYPES.COMPLETED:
      return {
        ...state,
        runStatus: 'completed',
        gate: null,
        runEndedAt: Date.now(),
        transcript: finalizeStreamingTail(state.transcript),
      }

    case UPDATE_TYPES.CANCELLED:
      return {
        ...state,
        runStatus: 'cancelled',
        gate: null,
        transcript: finalizeStreamingTail(state.transcript),
      }

    default:
      // node_added / edge_added / *_configured / evaluation_update / heartbeat /
      // save_draft_requested / unknown — no chat-state change here.
      return state
  }
}

/**
 * Bulk snapshot apply (rehydration / gap-resync). Replaces the transcript with
 * the server's, then re-appends any local `pending` user entries the server has
 * not yet persisted — a resync can never drop a just-sent message.
 */
/**
 * The id of the last `ai_message` entry per response_id — the one bubble of a
 * turn that carries the thumbs. Entries without a response_id never qualify.
 */
export const lastAiBubbleIds = (transcript: TranscriptEntry[]): Set<string> => {
  const lastByTurn = new Map<string, string>()
  for (const entry of transcript) {
    if (entry.role === 'user' || entry.update_type !== UPDATE_TYPES.AI_MESSAGE) continue
    if (!entry.response_id) continue
    lastByTurn.set(entry.response_id, entry.id)
  }
  return new Set(lastByTurn.values())
}

export const applySnapshot = (
  state: ReducedState & { sessionId: string | null },
  snapshot: ConversationSnapshot,
): ReducedState & { sessionId: string | null } => {
  // The persisted transcript also carries non-chat rows (evaluation_update,
  // node/canvas events, lifecycle markers) — the renderer nulls them but the
  // list wrapper would still emit an empty spaced row each (the blank-gap
  // regression). Keep exactly what renders: user rows (minus machine
  // round-trips), text entries, and chat-visible events.
  const server = (snapshot.ui_transcript ?? []).filter((e) => {
    if (e.role === 'user') return !(e.text ?? '').startsWith('[FE_EVENT]')
    if (e.message_type === 'text') return true
    return CHAT_VISIBLE_EVENTS.has(e.update_type)
  })
  // A snapshot must not re-key rows this tab already renders: live rows carry
  // local ids, server rows BE ids, and the list is keyed by id inside one
  // AnimatePresence — swapping every id remounts (and exit/enter-animates) the
  // whole transcript on the always-fired terminal true-up. A server row that IS
  // an existing row (same identity the pending-absorption below uses: role +
  // update_type + trimmed text + compatible response_id) keeps the id already
  // on screen; only genuinely new rows get new keys. Matches consume in order
  // so repeated identical rows (two tool calls in one turn) pair positionally.
  const prior = state.transcript
  const consumed = new Array<boolean>(prior.length).fill(false)
  const stableServer = server.map((row) => {
    const text = (row.text ?? '').trim()
    const match = prior.findIndex(
      (e, i) =>
        !consumed[i] &&
        e.role === row.role &&
        e.update_type === row.update_type &&
        (e.text ?? '').trim() === text &&
        (!e.response_id || e.response_id === (row.response_id ?? null)),
    )
    if (match === -1) return row
    consumed[match] = true
    return row.id === prior[match].id ? row : { ...row, id: prior[match].id }
  })
  const serverUserRows = server.filter((e) => e.role === 'user')
  const serverUserTexts = new Set(serverUserRows.map((e) => (e.text ?? '').trim()))
  // Key on response_id AND text, not response_id alone: the BE persists MORE
  // THAN ONE distinct-text user row under a single run response_id (the initial
  // prompt and a parked-gate free-text answer chained in the same turn), so a
  // bare response_id set would collapse them and wrongly absorb a genuinely new
  // local message that happens to share the id.
  const serverUserKeys = new Set(
    serverUserRows
      .filter((e) => e.response_id)
      .map((e) => `${e.response_id} ${(e.text ?? '').trim()}`),
  )
  // Survivors: entries this tab originated that the server snapshot does not
  // contain yet. `local` (not `pending`) is the persistence marker — a live
  // user_message event clears the dim state but an early gap-resync can still
  // carry a snapshot persisted before the turn. A row is absorbed once the
  // server carries the SAME (response_id, text) — so sending "yes" twice keeps
  // both, and two distinct messages under one response_id both survive; a row
  // with no response_id falls back to a trimmed-text match.
  const pendingTail = state.transcript.filter((e) => {
    if (!(e.pending || e.local)) return false
    const text = (e.text ?? '').trim()
    const absorbed = e.response_id
      ? serverUserKeys.has(`${e.response_id} ${text}`)
      : serverUserTexts.has(text)
    return !absorbed
  })
  const runStatus = snapshot.run_status ?? 'idle'
  return {
    ...state,
    sessionId: snapshot.session_id ?? state.sessionId,
    transcript: [...stableServer, ...pendingTail],
    feedback: snapshot.feedback ?? {},
    // Restore the progress tracker from the persisted snapshot; a snapshot
    // without one (older BE / never created) keeps whatever is live — the
    // terminal true-up must not wipe a card the run just streamed.
    ...(snapshot.checklist?.checklist?.length
      ? {
          checklist: snapshot.checklist.checklist,
          checklistExplanation: snapshot.checklist.explanation ?? null,
        }
      : {}),
    runStatus,
    gate:
      runStatus === 'running' || runStatus === 'waiting_input'
        ? gateFromInteraction(snapshot.pending_interaction)
        : null,
    // A reattached tab must be able to cancel: the run's response_id comes
    // from the snapshot, not only from a live `started` frame.
    responseId:
      runStatus === 'running' || runStatus === 'waiting_input'
        ? (snapshot.run_response_id ?? null)
        : null,
  }
}

interface VibeAgentStore extends VibeAgentState {
  dispatch: (event: VibeEvent) => void
  hydrate: (snapshot: ConversationSnapshot) => void
  appendPendingUserMessage: (text: string) => void
  answerGateLocally: (interactionId: string, answers: GateAnswer[] | null) => void
  setRunStatus: (status: RunStatus) => void
  setUserInput: (value: string) => void
  setChecklistExpanded: (expanded: boolean) => void
  setFeedback: (responseId: string, vote: FeedbackVote) => void
  clearFeedback: (responseId: string) => void
  enqueueMessage: (text: string) => void
  removeQueued: (id: string) => void
  editQueued: (id: string, text: string) => void
  reorderQueue: (orderedIds: string[]) => void
  resumeQueue: () => void
  clearQueue: () => void
  holdGateLocally: () => void
  reset: () => void
}

export const useVibeAgentStore = create<VibeAgentStore>((set) => ({
  ...initialState,

  dispatch: (event) =>
    set((s) => {
      const next = applyEvent(s, event)
      const sessionId = event.session_id ?? s.sessionId
      return next === s && sessionId === s.sessionId ? s : { ...next, sessionId }
    }),

  hydrate: (snapshot) => set((s) => applySnapshot(s, snapshot)),

  setFeedback: (responseId, vote) =>
    set((s) => ({ feedback: { ...s.feedback, [responseId]: vote } })),

  clearFeedback: (responseId) =>
    set((s) => {
      if (!(responseId in s.feedback)) return s
      const { [responseId]: _removed, ...rest } = s.feedback
      return { feedback: rest }
    }),

  appendPendingUserMessage: (text) =>
    set((s) => ({
      transcript: [
        // A free-text send while a question gate is armed IS the answer — resolve
        // the open card in the same set() the gate clears in, or it stays
        // interactive and can double-submit. Approval gates have no card.
        ...(s.gate?.kind === 'question'
          ? resolveGateEntry(s.transcript, s.gate.interactionId, null)
          : s.transcript),
        {
          id: v4(),
          role: 'user',
          message_type: 'text',
          update_type: UPDATE_TYPES.USER_MESSAGE,
          text,
          ts: Date.now(),
          pending: true,
          local: true,
        },
      ],
      runStatus: 'sending',
      gate: null,
    })),

  answerGateLocally: (interactionId, answers) =>
    set((s) => ({
      gate: null,
      runStatus: 'running',
      transcript: resolveGateEntry(s.transcript, interactionId, answers),
    })),

  setRunStatus: (runStatus) => set({ runStatus }),
  setUserInput: (userInput) => set({ userInput }),
  setChecklistExpanded: (checklistExpanded) => set({ checklistExpanded }),

  enqueueMessage: (text) =>
    set((s) => {
      const trimmed = text.trim()
      if (!trimmed) return s
      return { queuedMessages: [...s.queuedMessages, { id: v4(), text: trimmed }] }
    }),

  removeQueued: (id) =>
    set((s) => {
      const queuedMessages = s.queuedMessages.filter((q) => q.id !== id)
      // An emptied queue has nothing to hold — drop the pause with it so the
      // flag can't leak into the next batch of staged messages.
      return { queuedMessages, queuePaused: queuedMessages.length > 0 && s.queuePaused }
    }),

  resumeQueue: () => set({ queuePaused: false }),

  // Optimistic twin of the gate_timer_held event: the chip must drop on
  // click, not after the round-trip. Idempotent with the event's reducer.
  holdGateLocally: () =>
    set((s) =>
      s.gate && s.gate.autoAnswerSeconds !== null
        ? { runStatus: 'waiting_input', gate: { ...s.gate, autoAnswerSeconds: null } }
        : s,
    ),

  clearQueue: () => set({ queuedMessages: [], queuePaused: false }),

  // Full-order replacement from drag-reorder. Rejects any order that isn't a
  // permutation of the current queue — a drain/remove can land mid-drag, and
  // applying a stale order would resurrect or drop messages.
  reorderQueue: (orderedIds) =>
    set((s) => {
      if (orderedIds.length !== s.queuedMessages.length) return s
      const byId = new Map(s.queuedMessages.map((q) => [q.id, q]))
      const next: typeof s.queuedMessages = []
      for (const id of orderedIds) {
        const item = byId.get(id)
        if (!item) return s
        byId.delete(id)
        next.push(item)
      }
      return { queuedMessages: next }
    }),

  // Slot-preserving by construction (map, not remove+append): silently
  // reordering on edit is a known data-integrity bug in peer products.
  editQueued: (id, text) =>
    set((s) => {
      const trimmed = text.trim()
      if (!trimmed) return { queuedMessages: s.queuedMessages.filter((q) => q.id !== id) }
      return {
        queuedMessages: s.queuedMessages.map((q) => (q.id === id ? { ...q, text: trimmed } : q)),
      }
    }),

  reset: () => set({ ...initialState }),
}))

/** True while a turn is in flight AND no gate is armed — the queue's "busy"
 *  predicate. Any armed gate re-opens the answer path — typing while the
 *  countdown runs IS the answer (the BE lets a delivered human answer win over
 *  the auto-answer), and parked runs resume by answering. */
export const selectIsRunActive = (s: VibeAgentState): boolean =>
  (s.runStatus === 'sending' || s.runStatus === 'running') && s.gate === null

/** Statuses that auto-dispatch the queue head. Everything except `error`: a
 *  staged message is the user's stated next intent, so a stop or an abnormal
 *  end should still deliver it (a cancel ends the current turn, not the queue;
 *  an interrupted run resumes by sending). `error` alone holds — a human
 *  should read the failure before more work goes at it. */
const DRAIN_TRIGGERS: readonly RunStatus[] = ['completed', 'cancelled', 'interrupted']

/** Pure decision for the queue-drain effect: dispatch only on a TRANSITION
 *  into a drain trigger (a snapshot re-asserting `completed` must not re-fire)
 *  with no gate armed, something queued, and the queue not paused (an
 *  abnormal end holds the queue until the user resumes or clears it). */
export const shouldAutoDrain = (
  prev: RunStatus,
  next: RunStatus,
  gate: ActiveGate | null,
  queueLength: number,
  queuePaused: boolean,
): boolean =>
  queueLength > 0 && !queuePaused && gate === null && next !== prev && DRAIN_TRIGGERS.includes(next)

/** Dispatch the queue head through `send` — the single dispatch path for both
 *  the auto-drain (which runs it a microtask after the completed transition)
 *  and the manual "Send next" button. Re-reads live state at execution time so
 *  a racing edit/remove (head id changed) or an already-started run wins over
 *  a stale dispatch. Returns whether the head was sent. */
export const drainQueueHead = (send: (text: string) => void, expectedHeadId: string): boolean => {
  const s = useVibeAgentStore.getState()
  const head = s.queuedMessages[0]
  if (!head || head.id !== expectedHeadId || selectIsRunActive(s)) return false
  s.removeQueued(head.id)
  send(head.text)
  return true
}
